import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ROLE_LABELS, normRole } from "@/lib/permissions";
import { KeyRound, Phone, Loader2 } from "lucide-react";

const SetLoginDialog = ({ user, onClose, onSaved }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/credentials`, { email, password });
      toast.success(`Login set for ${user.name || user.phone}`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to set login"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl" data-testid="set-login-dialog">
        <DialogHeader><DialogTitle>Set email & password for {user.name || user.phone}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-xl" data-testid="set-login-email" /></div>
          <div><Label>Password (min 6 characters)</Label><Input type="text" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-xl" data-testid="set-login-password" /></div>
          <p className="text-xs text-slate-500">Share these details with the user so they can log in.</p>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="rounded-full bg-brand hover:bg-emerald-800 text-white" data-testid="set-login-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save login"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const NoLoginUsers = () => {
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    try { setUsers((await api.get("/admin/no-login-users")).data.users); } catch { setUsers([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (users.length === 0) return null;
  return (
    <Card className="mt-8 rounded-3xl border-amber-200 overflow-hidden" data-testid="no-login-users">
      <div className="px-5 py-4 bg-amber-50 border-b border-amber-100">
        <div className="font-head font-bold text-amber-900 flex items-center gap-2"><KeyRound className="h-4 w-4" /> Accounts without a login</div>
        <p className="text-xs text-amber-800/80 mt-0.5">These users signed up with their mobile number earlier. Set an email and password so they can log in again.</p>
      </div>
      <div className="divide-y divide-amber-50">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-4 px-5 py-3.5">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-800 truncate">{u.name || "—"}</div>
              <div className="text-xs text-slate-400 inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {u.phone}</div>
            </div>
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-200">{ROLE_LABELS[normRole(u.role)] || "Farmer"}</span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setTarget(u)} data-testid={`set-login-${u.id}`}>Set login</Button>
          </div>
        ))}
      </div>
      {target && <SetLoginDialog user={target} onClose={() => setTarget(null)} onSaved={() => { setTarget(null); load(); }} />}
    </Card>
  );
};
